import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const AboutSection = styled.section`
  padding: 80px 0 80px 48px;
  max-width: 900px;

  @media (max-width: 1080px) {
    padding: 80px 24px;
    max-width: none;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const Title = styled(motion.h2)`
  font-size: clamp(32px, 5vw, 48px);
  font-weight: 700;
  color: ${({ theme }) => theme.colors.white};
  margin-bottom: 12px;
`;

const TitleAccent = styled.span`
  background: linear-gradient(135deg, #7dd3fc, #60a5fa);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled(motion.p)`
  font-size: 16px;
  color: var(--clr-text-muted);
  letter-spacing: 2px;
  text-transform: uppercase;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const TextColumn = styled(motion.div)``;

const Paragraph = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: var(--clr-text-bright);
  margin-bottom: 20px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SkillsColumn = styled(motion.div)``;

const SkillCategory = styled.div`
  margin-bottom: 28px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const CategoryLabel = styled.h4`
  font-size: 12px;
  font-weight: 600;
  color: var(--clr-text-faint);
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 12px;
`;

const SkillTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SkillTag = styled(motion.span)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  background: var(--clr-accent-fill);
  color: var(--clr-text-bright);
  border: 1px solid var(--clr-accent-border);
  transition: all 0.2s ease;

  i {
    font-size: 16px;
  }

  img {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: var(--clr-accent-fill-h);
    border-color: var(--clr-accent-border-h);
    color: ${({ theme }) => theme.colors.text};
  }
`;

const skillIcons: Record<string, string> = {
  'AWS': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-plain-wordmark.svg',
  'Azure': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/azure/azure-original.svg',
  'Docker': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg',
  'Kubernetes': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/kubernetes/kubernetes-plain.svg',
  'Terraform': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/terraform/terraform-original.svg',
  'Ansible': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/ansible/ansible-original.svg',
  'GitLab CI/CD': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/gitlab/gitlab-original.svg',
  'GitHub Actions': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/githubactions/githubactions-original.svg',
  'Jenkins': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/jenkins/jenkins-original.svg',
  'TypeScript': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/typescript/typescript-original.svg',
  'Python': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg',
  'Java': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/java/java-original.svg',
  'Bash': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/bash/bash-original.svg',
  'Grafana': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/grafana/grafana-original.svg',
  'Prometheus': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/prometheus/prometheus-original.svg',
  'Helm': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/helm/helm-original.svg',
};

const skills = {
  'Cloud & DevOps': ['AWS', 'Azure', 'Docker', 'Kubernetes', 'Terraform', 'Ansible'],
  'CI/CD': ['GitLab CI/CD', 'GitHub Actions', 'Jenkins'],
  'Languages': ['TypeScript', 'Python', 'Java', 'Bash'],
  'Monitoring': ['Grafana', 'Prometheus'],
};

const tagVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: (i: number) => ({
    opacity: 1,
    scale: 1,
    transition: { delay: i * 0.03, duration: 0.3 },
  }),
};

const About: React.FC = () => {
  const { t } = useTranslation();
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  let tagIndex = 0;

  return (
    <AboutSection id="about" ref={ref}>
      <SectionHeader>
        <Subtitle
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          {t('about.subtitle')}
        </Subtitle>
        <Title
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          {t('about.title')} <TitleAccent>{t('about.titleAccent')}</TitleAccent>
        </Title>
      </SectionHeader>

      <ContentGrid>
        <TextColumn
          initial={{ opacity: 0, x: -30 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Paragraph>
            {t('about.paragraph1')}
          </Paragraph>
          <Paragraph>
            {t('about.paragraph2')}
          </Paragraph>
        </TextColumn>

        <SkillsColumn
          initial={{ opacity: 0, x: 30 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          {Object.entries(skills).map(([category, items]) => (
            <SkillCategory key={category}>
              <CategoryLabel>{t(`about.categories.${category}`, category)}</CategoryLabel>
              <SkillTags>
                {items.map((skill) => {
                  const i = tagIndex++;
                  return (
                    <SkillTag
                      key={skill}
                      custom={i}
                      variants={tagVariants}
                      initial="hidden"
                      animate={inView ? 'visible' : 'hidden'}
                      whileHover={{ y: -2 }}
                    >
                      {skillIcons[skill] && (
                        <img src={skillIcons[skill]} width="16" height="16" alt="" style={{ flexShrink: 0 }} />
                      )}
                      {skill}
                    </SkillTag>
                  );
                })}
              </SkillTags>
            </SkillCategory>
          ))}
        </SkillsColumn>
      </ContentGrid>
    </AboutSection>
  );
};

export default About;